import { useState } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Plus, Trash2, Loader2, ScanLine } from 'lucide-react';
import { UploadInput } from '../../UploadInput';
import { extractOCR } from '../../../services/api';
import { toast } from 'sonner';

const CardDetailsSection = ({ bankForm, updateForm }) => {
  const [scanning, setScanning] = useState(false);
  const cards = bankForm.debitCards || [{ cardNumber: '', expiry: '', cvv: '', pin: '', imageDataUrl: '' }];

  const updateCard = (index, field, value) => {
    const updated = [...cards];
    updated[index] = { ...updated[index], [field]: value };
    updateForm('debitCards', updated);
    // Keep legacy fields synced with first card
    if (index === 0) {
      const map = { cardNumber: 'debitCardNumber', expiry: 'debitCardExpiry', cvv: 'debitCardCVV', pin: 'debitCardPIN', imageDataUrl: 'debitCardImageDataUrl' };
      if (map[field]) updateForm(map[field], value);
    }
  };

  const addCard = () => {
    updateForm('debitCards', [...cards, { cardNumber: '', expiry: '', cvv: '', pin: '', imageDataUrl: '' }]);
  };

  const removeCard = (index) => {
    if (cards.length <= 1) return;
    const updated = cards.filter((_, i) => i !== index);
    updateForm('debitCards', updated);
  };

  const handleCardOCR = async (index, base64) => {
    updateCard(index, 'imageDataUrl', base64);
    setScanning(true);
    try {
      const res = await extractOCR(base64, 'debit_card');
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        if (d.cardNumber) updateCard(index, 'cardNumber', d.cardNumber);
        if (d.expiryDate) updateCard(index, 'expiry', d.expiryDate);
        toast.success('Card details extracted');
      } else {
        toast.info('Could not auto-read card. Please enter details manually.');
      }
    } catch {
      toast.info('OCR unavailable. Enter card details manually.');
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="space-y-4 p-4 bg-white border rounded-lg">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-slate-700">Debit Card Details</h3>
        <Button type="button" onClick={addCard} variant="outline" size="sm" className="gap-1"><Plus className="w-3 h-3" />Add Card</Button>
      </div>
      {cards.map((card, idx) => (
        <div key={`card-${idx}`} className="p-3 bg-slate-50 rounded-lg border space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">Card {idx + 1}</span>
            {cards.length > 1 && <Button type="button" onClick={() => removeCard(idx)} variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600"><Trash2 className="w-3 h-3" /></Button>}
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-xs"><ScanLine className="w-3 h-3 text-blue-600" />Upload Card Image {scanning && <Loader2 className="w-3 h-3 animate-spin text-blue-600" />}</Label>
            <UploadInput className="mt-0.5" onUpload={(b) => handleCardOCR(idx, b)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2"><Label className="text-xs">Card Number</Label><Input value={card.cardNumber || ''} onChange={(e) => updateCard(idx, 'cardNumber', e.target.value)} placeholder="XXXX XXXX XXXX XXXX" /></div>
            <div><Label className="text-xs">Expiry</Label><Input value={card.expiry || ''} onChange={(e) => updateCard(idx, 'expiry', e.target.value)} placeholder="MM/YY" /></div>
            <div><Label className="text-xs">CVV</Label><Input value={card.cvv || ''} onChange={(e) => updateCard(idx, 'cvv', e.target.value)} placeholder="XXX" maxLength="3" /></div>
            <div className="col-span-2"><Label className="text-xs">PIN</Label><Input value={card.pin || ''} onChange={(e) => updateCard(idx, 'pin', e.target.value)} placeholder="XXXX" maxLength="6" /></div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CardDetailsSection;
